import api from './api';

// NOTA: Las rutas ya incluyen /api desde el wrapper 'api'
// No agregar /api manualmente en las URLs

// Response Types
export interface GeneralKPIs {
  total_businesses: string;
  total_audits: string;
  completed_audits: string;
  draft_audits: string;
  avg_compliance: string;
  total_auditors: string;
}

export interface AuditorKPIs {
  my_audits: string;
  completadas: string;
  borradores: string;
  my_average: string;
  assigned_businesses: string;
  audits_week: string;
}

export interface ManagerKPIs {
  last_audit_id: number;
  last_score: string;
  last_date: string;
  low_answers: string;
  quarter_average: string;
}

export interface Trends {
  audits_month_current: string;
  audits_month_previous: string;
  avg_month_current: string;
  avg_month_previous: string | null;
}

export interface KPIsResponse {
  kpis: GeneralKPIs | AuditorKPIs | ManagerKPIs;
  trends?: Trends;
}

export interface RecentAudit {
  id: number;
  compliance_percentage: string | null;
  start_date: string;
  end_date: string | null;
  business_name: string;
  business_timezone?: string;
  auditor_name: string;
  form_name: string;
  low_answers: string;
}

export interface PeriodTrend {
  period: string;
  total_audits: string;
  avg_compliance: string;
  minimum: string;
  maximum: string;
}

export interface CategoryTrend {
  period: string;
  category_name: string;
  total_responses: string;
  avg_compliance: string;
}

export interface TrendsResponse {
  general?: PeriodTrend[];
  byCategory?: CategoryTrend[];
}

export interface BusinessRanking {
  id: number;
  name: string;
  address: string;
  total_audits: string;
  avg_compliance: string | null;
  last_audit: string | null;
  good_audits: string;
  bad_audits: string;
}

export interface AuditorRanking {
  id: number;
  name: string;
  email: string;
  total_audits: string;
  avg_compliance: string;
  audited_businesses: string;
  last_audit: string;
}

export interface CategoryRanking {
  id: number;
  name: string;
  description: string;
  total_responses: string;
  avg_compliance: string;
}

export type RankingsResponse = BusinessRanking[] | AuditorRanking[] | CategoryRanking[];

export interface Distribution {
  status: string;
  count: string;
  percentage: string;
}

export interface Alert {
  id?: number;
  type: string;
  priority?: string;
  category?: string;
  message: string;
  count?: string;
  audit_id?: number | null;
  business_id?: number | null;
  business_name?: string | null;
  created_at?: string;
  read?: boolean;
  details?: Array<{
    name: string;
    average: string;
  }>;
}

export interface BusinessEvolution {
  business_id: number;
  business_name: string;
  form_id: number;
  form_name: string;
  last_compliance: number;
  prev_compliance: number | null;
}

export interface CategoryComparison {
  category_name: string;
  form_name: string;
  form_id: number;
  business_id: number;
  business_name: string;
  color: string | null;
  last_score: number | null;
  previous_score: number | null;
}

class AnalyticsService {
  async getKPIs(): Promise<KPIsResponse> {
    try {
      const response = await api.get('/analytics/kpis');
      // Backend devuelve { success: true, data: { kpis: {...}, trends: {...} } }
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error getting KPIs:', error);
      throw error;
    }
  }

  async getRecentAudits(limit: number = 5): Promise<RecentAudit[]> {
    try {
      const response = await api.get(`/analytics/recent-audits?limit=${limit}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error getting recent audits:', error);
      throw error;
    }
  }

  async getTrends(params: { type: 'general' | 'category'; startDate?: string; endDate?: string; categoryId?: number }): Promise<PeriodTrend[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('type', params.type);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.categoryId) queryParams.append('categoryId', params.categoryId.toString());

      const response = await api.get(`/analytics/trends?${queryParams.toString()}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error getting trends:', error);
      throw error;
    }
  }

  async getRankings(params: { type: 'businesses' | 'auditors' | 'categories'; limit?: number; order?: 'ASC' | 'DESC' }): Promise<RankingsResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('type', params.type);
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.order) queryParams.append('order', params.order);

      const response = await api.get(`/analytics/rankings?${queryParams.toString()}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error getting rankings:', error);
      throw error;
    }
  }

  async getDistribution(): Promise<Distribution[]> {
    try {
      const response = await api.get('/analytics/distribution');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error getting distribution:', error);
      throw error;
    }
  }

  async getAlerts(params?: { read?: boolean; limit?: number }): Promise<Alert[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.read !== undefined) queryParams.append('read', params.read.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const response = await api.get(`/analytics/alerts?${queryParams.toString()}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error getting alerts:', error);
      throw error;
    }
  }

  async markAlertAsRead(alertId: number): Promise<void> {
    try {
      await api.put(`/analytics/alerts/${alertId}/read`);
    } catch (error) {
      console.error('Error marking alert as read:', error);
      throw error;
    }
  }

  async getBusinessEvolution(): Promise<BusinessEvolution[]> {
    try {
      const response = await api.get('/analytics/business-evolution');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error getting business evolution:', error);
      throw error;
    }
  }

  async getCategoryComparison(): Promise<CategoryComparison[]> {
    try {
      const response = await api.get('/analytics/category-comparison');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error getting category comparison:', error);
      throw error;
    }
  }
}

export default new AnalyticsService();
